import { z } from "zod";

const minor = z.number().int().nonnegative();
const currency = z.string().regex(/^[A-Z]{3}$/);
const provider = z.enum(["discount_codes", "membership", "external"]);
const provenance = z.object({
  kind: z.string().min(1),
  label: z.string().min(1),
  ref: z.string().nullable(),
  plan_id: z.string().nullable().optional(),
  membership_id: z.string().nullable().optional(),
  plan_name: z.string().min(1).nullable().optional(),
  display: z.string().min(1).nullable().optional(),
});
const effect = z.object({
  id: z.string().min(1),
  provider_kind: provider,
  provider_id: z.string().nullable(),
  type: z.enum([
    "percent_off",
    "amount_off",
    "price_override",
    "fee_waiver",
    "unlock",
  ]),
  state: z.enum(["applied", "unapplied"]),
  source: provenance.nullable(),
  requires: provenance.nullable(),
  description: z.string().min(1),
  stackable: z.boolean(),
  consumable: z.boolean(),
  redemption_ref: z.string().min(1).nullable(),
  percent_bps: z.number().int().nullable(),
  amount_minor: z.number().int().nullable(),
  currency: currency.nullable(),
  price_override_minor: minor.nullable(),
  applies_to_item_ids: z.array(z.string()),
  applies_to_event_ids: z.array(z.string()),
  unlocks_item_ids: z.array(z.string()),
});

// Lissen's validated output boundary, without a server/database dependency in clients.
export const engineQuoteSchema = z.object({
  kind: z.literal("engine"),
  quote: z.object({
    mode: z.enum(["preview", "checkout_quote"]),
    created: z.string().datetime({ offset: true }),
    currency,
    subject: z.object({
      kind: z.enum(["authenticated", "anonymous"]),
      customer_id: z.string().nullable(),
      external_ref: z.string().nullable(),
    }),
    line_items: z.array(
      z.object({
        item_id: z.string(),
        event_id: z.string().nullable(),
        description: z.string(),
        quantity: z.number().int().positive(),
        unit_list_price_minor: minor,
        unit_price_minor: minor,
        list_subtotal_minor: minor,
        discount_minor: minor,
        subtotal_minor: minor,
        booking_fee_minor: minor,
        platform_fee_minor: minor,
        effect_ids: z.array(z.string().min(1)),
        sellable: z.boolean(),
        blocked_reason: z
          .enum([
            "gated",
            "inactive",
            "sale_not_started",
            "sale_ended",
            "approval_required",
            "event_cancelled",
          ])
          .nullable(),
      }),
    ),
    applied_effects: z.array(effect),
    unapplied_effects: z.array(effect),
    superseded_effect_ids: z.array(z.string().min(1)),
    rejected_codes: z.array(
      z.object({
        code: z.string(),
        reason: z.enum([
          "not_found",
          "inactive",
          "not_yet_valid",
          "expired",
          "not_applicable",
          "currency_mismatch",
          "max_uses_reached",
          "per_customer_limit_reached",
        ]),
      }),
    ),
    totals: z.object({
      list_subtotal_minor: minor,
      discount_minor: minor,
      subtotal_minor: minor,
      booking_fee_minor: minor,
      platform_fee_minor: minor,
      total_minor: minor,
    }),
    degraded: z.boolean(),
    degraded_providers: z.array(provider),
  }),
});
